import { useParams } from 'react-router-dom';
import { Search, EllipsisVertical, Eye } from 'lucide-react';
import { useState } from 'react';

import { Error } from '@/components/ui/error';
import { Loading } from '@/components/ui/loading';
import { Empty, EmptyIcon, EmptyTitle, EmptyDescription } from '@/components/ui/empty';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

import ViewTogalIntegrationDetailsDialog from '@/pages/togalIntegration/ViewTogalIntegrationDetailsDialog';
import ViewZebelApiIntegrationDetailsDialog from '@/pages/zebelApiIntegration/ViewZebelApiIntegrationDetailsDialog';

import { useZebelApiIntegration } from '@/data/zebelApiIntegration';
import { useTogalIntegrations } from '@/data/togalIntegration';

const Integrations = () => {
  const params = useParams();
  const zebelApiIntegration = useZebelApiIntegration(params.companyUid);
  const togalIntegrations = useTogalIntegrations(params.companyUid);
  const [openedIntegration, setOpenedIntegration] = useState(null);

  if (zebelApiIntegration.isLoading && !zebelApiIntegration.data) {
    return <Loading />;
  }
  if (togalIntegrations.isLoading && !togalIntegrations.data) {
    return <Loading />;
  }

  if (zebelApiIntegration.error) {
    return <Error message="An error occurred loading company zebel api integration" />;
  }
  if (togalIntegrations.error) {
    return <Error message="An error occurred loading company togal integrations" />;
  }

  const tableData = [];
  if (zebelApiIntegration.data) {
    tableData.push({ ...zebelApiIntegration.data, name: 'Zebel API' });
  }

  if (togalIntegrations.data && togalIntegrations.data.length > 0) {
    const firstIntegration = togalIntegrations.data[0];
    tableData.push({
      ...firstIntegration,
      name: 'Togal API',
      create_ts: firstIntegration.create_ts
    });
  }

  const handleOpenViewDetails = (integration) => {
    setOpenedIntegration(integration.name);
  };

  let content = (
    <Empty>
      <EmptyIcon>
        <Search />
      </EmptyIcon>
      <EmptyTitle>No Integrations Installed</EmptyTitle>
      <EmptyDescription>No integrations installed for this company</EmptyDescription>
    </Empty>
  );

  if (tableData.length > 0) {
    content = (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Installed</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {tableData.map((integration) => (
            <TableRow key={integration.uid}>
              <TableCell className="font-medium">{integration.name}</TableCell>
              <TableCell>
                <Badge variant="success">{integration.status}</Badge>
              </TableCell>
              <TableCell>
                {new Date(integration.create_ts).toLocaleDateString(undefined, {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric'
                })}
              </TableCell>
              <TableCell className="text-right">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="icon" size="icon" aria-label="Open menu" className="shrink-0">
                      <EllipsisVertical />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuLabel>{integration.name}</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => handleOpenViewDetails(integration)}>
                      <Eye />
                      View Details
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  return (
    <>
      <div className="space-y-4">
        <h2 className="text-xl font-semibold">Integrations</h2>
        {content}
      </div>
      {openedIntegration === 'Togal API' && <ViewTogalIntegrationDetailsDialog onClose={() => setOpenedIntegration(null)} companyUid={params.companyUid} />}
      {openedIntegration === 'Zebel API' && <ViewZebelApiIntegrationDetailsDialog onClose={() => setOpenedIntegration(null)} companyUid={params.companyUid} />}
    </>
  );
};

export default Integrations;
